import React from 'react';

export default function useFitSingleLineText(opts: {
  containerRef: React.RefObject<HTMLElement>;
  textRef: React.RefObject<HTMLElement>;
  deps: React.DependencyList;
  minPx?: number;
  maxPx?: number;
  extraWidthPaddingPx?: number; // safety margin
}) {
  const { containerRef, textRef, deps, minPx = 10, maxPx = 28, extraWidthPaddingPx = 2 } = opts;

  React.useLayoutEffect(() => {
    const container = containerRef.current;
    const textEl = textRef.current;
    if (!container || !textEl) return;

    const fit = () => {
      const availableW = Math.max(0, container.clientWidth - extraWidthPaddingPx);
      if (availableW <= 0) return;

      // Reset to max before measuring so we have consistent baseline
      textEl.style.fontSize = `${maxPx}px`;
      textEl.style.whiteSpace = 'nowrap';

      let lo = minPx;
      let hi = maxPx;

      // If it already fits at max, keep it.
      if (textEl.scrollWidth <= availableW) {
        textEl.style.fontSize = `${maxPx}px`;
        return;
      }

      // Binary search for best fit
      while (lo < hi) {
        const mid = Math.floor((lo + hi) / 2);
        textEl.style.fontSize = `${mid}px`;

        if (textEl.scrollWidth <= availableW) {
          lo = mid + 1;
        } else {
          hi = mid;
        }
      }

      // lo is first that might overflow; step back one
      const finalPx = Math.max(minPx, lo - 1);
      textEl.style.fontSize = `${finalPx}px`;
    };

    // Fit once now
    fit();

    // Refit on resize (important for print preview / zoom / etc.)
    const ro = new ResizeObserver(() => fit());
    ro.observe(container);

    // If fonts load after first render, metrics can change
    // (This is safe even if document.fonts is undefined)
    const fonts: any = (document as any).fonts;
    if (fonts?.ready?.then) {
      fonts.ready.then(() => fit()).catch(() => {});
    }

    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
